import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import API from "../lib/api";
import { Monitor, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const BEAT_MS = 30000;

/* Invisible monitoring agent: sends 30s heartbeats with in-app activity COUNTS
   (never keystroke content). Handles idle detection and optional consent-based
   screen snapshots when enabled by an Operations Director. */
export default function MonitoringAgent() {
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  const kbRef = useRef(0);
  const mouseRef = useRef(0);
  const lastEventRef = useRef(Date.now());
  const lastMoveCountRef = useRef(0);
  const [settings, setSettings] = useState(null);
  const [monitored, setMonitored] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [pillDismissed, setPillDismissed] = useState(false);
  const streamRef = useRef(null);
  const shotTimerRef = useRef(null);
  const videoRef = useRef(null);

  pathRef.current = location.pathname;

  useEffect(() => {
    const onKey = () => { kbRef.current += 1; lastEventRef.current = Date.now(); };
    const onMouse = () => { mouseRef.current += 1; lastEventRef.current = Date.now(); };
    const onMove = () => {
      const now = Date.now();
      if (now - lastMoveCountRef.current > 1000) { mouseRef.current += 1; lastMoveCountRef.current = now; }
      lastEventRef.current = now;
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onMouse);
    window.addEventListener("wheel", onMove, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("touchstart", onMove, { passive: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onMouse);
      window.removeEventListener("wheel", onMove);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchstart", onMove);
    };
  }, []);

  useEffect(() => {
    let stopped = false;
    const beat = async () => {
      const idleThresholdMs = ((settings?.idle_threshold_minutes) || 5) * 60000;
      const idle = document.hidden || Date.now() - lastEventRef.current > idleThresholdMs;
      const payload = { path: pathRef.current, keyboard_events: kbRef.current, mouse_events: mouseRef.current, idle };
      kbRef.current = 0;
      mouseRef.current = 0;
      try {
        const { data } = await API.post("/monitoring/heartbeat", payload);
        if (stopped) return;
        setMonitored(data.monitored);
        setSettings(data.settings || null);
      } catch { /* silent */ }
    };
    beat();
    const iv = setInterval(beat, BEAT_MS);
    return () => { stopped = true; clearInterval(iv); };
  }, [settings?.idle_threshold_minutes]); // eslint-disable-line react-hooks/exhaustive-deps

  const captureFrame = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement("canvas");
    const scale = Math.min(1, 1280 / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = canvas.toDataURL("image/jpeg", 0.45);
    try { await API.post("/monitoring/screenshot", { image }); } catch { /* silent */ }
  }, []);

  const stopSharing = useCallback(() => {
    if (shotTimerRef.current) clearInterval(shotTimerRef.current);
    shotTimerRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    videoRef.current = null;
    setSharing(false);
  }, []);

  const startSharing = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 1 } });
      streamRef.current = stream;
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      videoRef.current = video;
      stream.getVideoTracks()[0].addEventListener("ended", stopSharing);
      setSharing(true);
      toast.success("Screen sharing enabled for monitoring");
      setTimeout(captureFrame, 4000);
      const intervalMs = ((settings?.screenshot_interval_minutes) || 10) * 60000;
      shotTimerRef.current = setInterval(captureFrame, intervalMs);
    } catch { /* user declined */ }
  };

  useEffect(() => () => stopSharing(), [stopSharing]);

  if (!monitored || !settings?.screenshots_enabled || sharing || pillDismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 flex items-center gap-1.5 rounded-full border border-border bg-card shadow-lg pl-3 pr-1.5 py-1.5" data-testid="monitoring-consent-pill">
      <Monitor size={14} className="text-primary" />
      <button onClick={startSharing} className="text-xs font-medium hover:underline" data-testid="enable-screen-sharing-btn">
        Enable screen sharing (monitoring)
      </button>
      <button onClick={() => setPillDismissed(true)} className="text-muted-foreground hover:text-foreground p-1" title="Dismiss for this session" data-testid="dismiss-monitoring-pill">
        <X size={12} />
      </button>
    </div>
  );
}
